import { MAX_STAGE_HEIGHT_CM } from '@/version-types';

// 1英寸（inch）等于2.54厘米
const ONE_INCH_CM = 2.54;
// 每英寸像素密度
export const DEFAULT_DPI = 72;

let DPI = 300; // 涉及px <--> mm转换时， dpi固定300

/**
 * 设置 DPI —— 设计师编辑器会有不同分辨率
 *
 * @param dpi - 分辨率
 */
export function setDPI(dpi = DEFAULT_DPI) {
    DPI = dpi;
}

/**
 * 获取分辨率
 */
export function getDPI() {
    return DPI;
}

/**
 * 是否为默认 DPI
 *
 * @param dpi - 分辨率
 */
export function isDefaultDPI() {
    return DPI === DEFAULT_DPI;
}

/**
 * 单位类型
 */
export enum SizeUnitType {
    px = 'px',
    mm = 'mm',
    cm = 'cm',
    in = 'in',
}

/**
 * 保留几位小数
 */
export const SizeUnitPrecision = {
    px: 0,
    mm: 0,
    cm: 1,
    in: 2,
};

/**
 * 像素转厘米
 *
 * @param pixels - 像素值
 */
export function pxToCM(pixels: number): number {
    const centimeters = (pixels / DPI) * ONE_INCH_CM;

    return Math.min(parseFloat(centimeters.toFixed(SizeUnitPrecision.cm)), MAX_STAGE_HEIGHT_CM);
}

/**
 * 像素转毫米
 *
 * @param pixels - 像素值
 */
export function pxToMM(pixels: number): number {
    const millimeters = (pixels / DPI) * (ONE_INCH_CM * 10);
    return Math.min(Math.round(millimeters), MAX_STAGE_HEIGHT_CM * 10);
}

/**
 * 像素转英寸
 *
 * @param pixels - 像素值
 * @param dpi - 可选；分辨率；默认值为 `72`
 */
export function pxToInch(pixels: number, dpi = 72): number {
    const inches = pixels / dpi;
    return parseFloat(inches.toFixed(SizeUnitPrecision.in));
}

/**
 * 厘米转像素
 *
 * @param centimeters - 厘米
 */
export function cmToPx(centimeters: number): number {
    const pixels = (centimeters * DPI) / ONE_INCH_CM;
    return ~~pixels;
}

/**
 * 毫米转像素
 *
 * @param millimeters - 毫米
 */
export function mmToPx(millimeters: number): number {
    const pixels = (millimeters * DPI) / (ONE_INCH_CM * 10);
    return ~~pixels;
}

/**
 * 英寸转像素
 *
 * @param inches - 英寸
 */
export function inchToPx(inches: number, dpi = 72): number {
    return ~~(inches * dpi);
}

/**
 * 转换像素到指定单位
 *
 * @param pixels - 像素值
 * @param unit - 单位
 * @param dpi - 可选；分辨率 DPI
 */
export function parseToUnit(pixels: number, unit: SizeUnitType, dpi?: number) {
    switch (unit) {
        case SizeUnitType.cm:
            return pxToCM(pixels);
        case SizeUnitType.mm:
            return pxToMM(pixels);
        case SizeUnitType.in:
            return pxToInch(pixels, dpi);
        default:
            return ~~pixels;
    }
}

/**
 * 把值转换成像素
 *
 * @param value - 要转换的值
 * @param unit - 单位
 * @param dpi - 可选；分辨率 DPI
 */
export function parseToPixels(value: number, unit: SizeUnitType, dpi?: number) {
    switch (unit) {
        case SizeUnitType.cm:
            return cmToPx(value);
        case SizeUnitType.mm:
            return mmToPx(value);
        case SizeUnitType.in:
            return inchToPx(value, dpi);
        default:
            return ~~value;
    }
}

/**
 * 枚举检查定义
 */
export interface IEnumCheck<T extends string | number> {
    isValid(value: any): value is T;
}

/**
 * 创建一个枚举检查类型
 *
 * @example `const check:IEnumCheck<SizeUnitType> = createEnumCheck(SizeUnitType);`
 * @example `check.isValid(1);`
 * @param enumObj - 枚举值
 */
export function createEnumCheck<T extends string | number>(enumObj: Record<string, T>): IEnumCheck<T> {
    const enumValues = Object.values(enumObj);
    // console.log(enumObj);

    return {
        isValid(value: any): value is T {
            return enumValues.includes(value);
        },
    };
}
/**
 * 移除带有前缀的classname
 * @param element
 * @param prefix
 */
export function removeClassesByPrefix(element, prefix) {
    for (let i = element.classList.length - 1; i >= 0; i--) {
        const className = element.classList[i];
        if (className.startsWith(prefix)) {
            element.classList.remove(className);
        }
    }
}

/**
 * SizeUnitType 枚举检查
 */
export const CheckSizeUnitType: IEnumCheck<SizeUnitType> = createEnumCheck(SizeUnitType) as IEnumCheck<SizeUnitType>;
